import { SiteHeader } from "@/components/site-header"

export default function AdminGuidePage() {
  return (
    <>
      <SiteHeader title="Guía de Administración" />
      <div className="flex flex-1 flex-col px-4 py-6 lg:px-6">
        <div className="w-full space-y-8">

          {/* ─── Roles ─── */}
          <section>
            <h2 className="text-base font-semibold mb-3">Roles de usuario</h2>
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium">Rol</th>
                    <th className="text-left px-4 py-2 font-medium">Descripción</th>
                    <th className="text-left px-4 py-2 font-medium">Acceso</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  <tr>
                    <td className="px-4 py-2 font-mono text-xs">admin</td>
                    <td className="px-4 py-2">Acceso total al sistema</td>
                    <td className="px-4 py-2 text-xs text-muted-foreground">Todo — incluye crear/editar/eliminar/restaurar en todos los módulos</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 font-mono text-xs">operator</td>
                    <td className="px-4 py-2">Operador de control central</td>
                    <td className="px-4 py-2 text-xs text-muted-foreground">Lectura en todos los módulos. Sin acceso a crear, editar ni eliminar registros</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 font-mono text-xs">fleet_manager</td>
                    <td className="px-4 py-2">Gestión de flota asignada</td>
                    <td className="px-4 py-2 text-xs text-muted-foreground">Solo su flota</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 font-mono text-xs">driver</td>
                    <td className="px-4 py-2">Acceso de conductor</td>
                    <td className="px-4 py-2 text-xs text-muted-foreground">Acceso mínimo</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* ─── Módulos y permisos ─── */}
          <section>
            <h2 className="text-base font-semibold mb-3">Módulos del sistema</h2>
            <p className="text-sm text-muted-foreground mb-3">
              El sidebar agrupa las funcionalidades en cuatro áreas. Solo el rol <span className="font-mono text-xs">admin</span> puede crear, editar y eliminar registros en todas ellas.
            </p>
            <div className="space-y-3">
              {[
                {
                  label: "Geografía",
                  desc: "Países, Provincias, Ciudades y Trayectorias, Rutas",
                  note: "Admin y Operator tienen lectura. Solo Admin puede gestionar."
                },
                {
                  label: "Viajes",
                  desc: "Tipos de servicio (CF, CN, RT, VV…)",
                  note: "Catálogo de clasificaciones para los viajes."
                },
                {
                  label: "Cargas — Productos",
                  desc: "Catálogo de mercadería transportada (ACEITE, CARNE, LECHE…)",
                  note: "Más de 200 productos base cargados desde el seeder."
                },
                {
                  label: "Cargas — Tipos de carga",
                  desc: "Clasificación de alto nivel de la carga: DE, LT, NP, PE",
                  note: "4 tipos base provistos por el sistema. Código solo letras A–Z, máximo 10 caracteres."
                },
              ].map(m => (
                <div key={m.label} className="border rounded-lg p-3 text-sm">
                  <div className="font-medium mb-0.5">{m.label}</div>
                  <div className="text-muted-foreground text-xs">{m.desc}</div>
                  <div className="text-xs mt-1 text-muted-foreground border-t pt-1">{m.note}</div>
                </div>
              ))}
            </div>
          </section>

          {/* ─── Soft delete / Restore ─── */}
          <section>
            <h2 className="text-base font-semibold mb-3">Borrado y restauración de registros</h2>
            <p className="text-sm text-muted-foreground mb-3">
              Ningún registro se elimina físicamente de la base de datos. El botón <span className="font-medium">Eliminar</span> realiza un <span className="font-medium">borrado lógico</span> (soft delete): el registro queda marcado con la fecha de eliminación y desaparece de la vista activa.
            </p>
            <div className="space-y-2 text-sm">
              <div className="flex gap-3 items-start">
                <span className="mt-0.5 size-5 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-xs shrink-0 font-bold">↩</span>
                <div>
                  <span className="font-medium">Restaurar: </span>
                  <span className="text-muted-foreground">usá el filtro <span className="font-mono text-xs bg-muted px-1 rounded">Eliminados</span> para ver los registros borrados. El ícono verde de restaurar reemplaza los botones de editar y eliminar.</span>
                </div>
              </div>
              <div className="flex gap-3 items-start">
                <span className="mt-0.5 size-5 rounded-full bg-muted text-muted-foreground flex items-center justify-center text-xs shrink-0 font-bold">i</span>
                <div className="text-muted-foreground">
                  Los registros eliminados no pueden editarse. Si necesitás modificar un registro borrado, primero restoralo y luego editalo.
                </div>
              </div>
            </div>
            <div className="mt-3 border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium">Módulo</th>
                    <th className="text-left px-4 py-2 font-medium">Soporta soft delete</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {[
                    ["Provincias", "✓"],
                    ["Ciudades y Trayectorias", "✓"],
                    ["Rutas", "✓"],
                    ["Tipos de servicio", "✓"],
                    ["Productos", "✓"],
                    ["Tipos de carga", "✓"],
                    ["Países", "—  (eliminación física)"],
                    ["Usuarios", "✓  (campo deletedAt)"],
                  ].map(([mod, val]) => (
                    <tr key={mod}>
                      <td className="px-4 py-2">{mod}</td>
                      <td className="px-4 py-2 text-xs text-muted-foreground">{val}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* ─── Tipos de carga ─── */}
          <section>
            <h2 className="text-base font-semibold mb-3">Gestión de Tipos de carga</h2>
            <p className="text-sm text-muted-foreground mb-3">
              Los tipos de carga clasifican la mercadería a alto nivel. Se acceden desde <span className="font-medium">Cargas → Tipos de carga</span> en el sidebar.
            </p>
            <div className="border rounded-lg overflow-hidden mb-3">
              <table className="w-full text-sm">
                <thead className="bg-muted">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium">Código</th>
                    <th className="text-left px-4 py-2 font-medium">Nombre</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {[["DE","Devoluciones"],["LT","LATA"],["NP","No Perecedero"],["PE","Perecedero"]].map(([code, name]) => (
                    <tr key={code}>
                      <td className="px-4 py-2 font-mono text-xs">{code}</td>
                      <td className="px-4 py-2">{name}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="bg-muted rounded-lg px-4 py-3 text-sm space-y-1">
              <div><span className="font-medium">Código:</span> solo letras A–Z, máximo 10 caracteres. Se convierte a mayúsculas automáticamente.</div>
              <div><span className="font-medium">Nombre:</span> hasta 200 caracteres.</div>
              <div><span className="font-medium">Unicidad:</span> no se pueden repetir códigos (activos ni eliminados).</div>
            </div>
          </section>

          {/* ─── Sesión ─── */}
          <section>
            <h2 className="text-base font-semibold mb-3">Sesiones de usuario</h2>
            <p className="text-sm text-muted-foreground mb-3">
              Cada login registra una sesión con IP, navegador y fecha de expiración (8 horas). Al hacer logout la sesión se elimina inmediatamente.
            </p>
            <p className="text-sm text-muted-foreground mb-2">
              Cuando una sesión expira, el sistema muestra un modal bloqueante informando al usuario que debe volver a iniciar sesión. No se redirige de forma abrupta.
            </p>
            <p className="text-sm text-muted-foreground">
              Para limpiar sesiones expiradas manualmente:
            </p>
            <pre className="bg-muted rounded-lg px-4 py-3 text-xs mt-2 overflow-x-auto">
              DELETE FROM sessions WHERE expires_at &lt; NOW();
            </pre>
          </section>

          {/* ─── Rate limiting ─── */}
          <section>
            <h2 className="text-base font-semibold mb-3">Bloqueo por intentos fallidos</h2>
            <p className="text-sm text-muted-foreground mb-3">
              El sistema bloquea los intentos de login por dirección IP para prevenir ataques de fuerza bruta.
            </p>
            <div className="bg-muted rounded-lg px-4 py-3 text-sm space-y-1">
              <div><span className="font-medium">Máximo:</span> 5 intentos fallidos</div>
              <div><span className="font-medium">Ventana:</span> 15 minutos</div>
              <div><span className="font-medium">Desbloqueo:</span> automático al cumplirse los 15 minutos</div>
            </div>
            <div className="mt-3 p-3 border border-yellow-200 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-950 rounded-lg text-sm text-yellow-800 dark:text-yellow-200">
              <span className="font-medium">Desbloqueo de urgencia:</span> reiniciar el proceso de la API limpia todos los bloqueos activos.
              <br />
              <code className="text-xs mt-1 block">docker compose restart api</code>
            </div>
          </section>

          {/* ─── Crear admin ─── */}
          <section>
            <h2 className="text-base font-semibold mb-3">Crear un usuario administrador</h2>
            <p className="text-sm text-muted-foreground mb-3">
              Generar el hash de la contraseña desde el servidor:
            </p>
            <pre className="bg-muted rounded-lg px-4 py-3 text-xs overflow-x-auto">{`cd apps/api && bun -e "console.log(await Bun.password.hash('TuContraseña'))"`}</pre>
            <p className="text-sm text-muted-foreground mt-3 mb-2">Luego insertar en la base de datos:</p>
            <pre className="bg-muted rounded-lg px-4 py-3 text-xs overflow-x-auto">{`INSERT INTO users (email, password_hash, first_name, last_name, role, is_active)
VALUES ('usuario@example.com', '<hash>', 'Nombre', 'Apellido', 'admin', true);`}</pre>
          </section>

          {/* ─── Seeds ─── */}
          <section>
            <h2 className="text-base font-semibold mb-3">Datos iniciales (seeders)</h2>
            <p className="text-sm text-muted-foreground mb-3">
              Al inicializar el sistema en un entorno nuevo, ejecutar los seeders en orden:
            </p>
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium">Comando</th>
                    <th className="text-left px-4 py-2 font-medium">Qué carga</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {[
                    ["bun run db:seed:users", "Usuarios de desarrollo (admin + operador)"],
                    ["bun run db:seed:countries", "Países del Mercosur + Chile + Bolivia"],
                    ["bun run src/db/seeds/states.ts", "Provincias y estados (~71 registros)"],
                    ["bun run src/db/seeds/cities.ts", "Ciudades y localidades (~1560 registros)"],
                    ["bun run src/db/seeds/routes.ts", "Rutas / Trayectos"],
                    ["bun run db:seed:service-types", "Tipos de servicio (CF, CN, RT, VV…)"],
                    ["bun run db:seed:products", "Productos de carga (~200 registros)"],
                    ["bun run db:seed:cargo-types", "Tipos de carga: DE, LT, NP, PE"],
                    ["bun run db:seed", "Todo lo anterior de una vez"],
                    ["bun run db:fresh", "Reset completo + push + seed (solo desarrollo)"],
                  ].map(([cmd, desc]) => (
                    <tr key={cmd}>
                      <td className="px-4 py-2 font-mono text-xs whitespace-nowrap">{cmd}</td>
                      <td className="px-4 py-2 text-xs text-muted-foreground">{desc}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* ─── Variables de entorno ─── */}
          <section>
            <h2 className="text-base font-semibold mb-3">Variables de entorno</h2>
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium">Variable</th>
                    <th className="text-left px-4 py-2 font-medium">Descripción</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  <tr><td className="px-4 py-2 font-mono text-xs">JWT_SECRET</td><td className="px-4 py-2 text-xs text-muted-foreground">Clave para firmar tokens JWT — debe ser larga y aleatoria</td></tr>
                  <tr><td className="px-4 py-2 font-mono text-xs">API_KEY</td><td className="px-4 py-2 text-xs text-muted-foreground">Requerida en header X-API-Key en todos los requests</td></tr>
                  <tr><td className="px-4 py-2 font-mono text-xs">CORS_ORIGIN</td><td className="px-4 py-2 text-xs text-muted-foreground">En prod: URL exacta del frontend</td></tr>
                  <tr><td className="px-4 py-2 font-mono text-xs">DATABASE_URL</td><td className="px-4 py-2 text-xs text-muted-foreground">Conexión a PostgreSQL</td></tr>
                  <tr><td className="px-4 py-2 font-mono text-xs">VITE_API_URL</td><td className="px-4 py-2 text-xs text-muted-foreground">URL base del backend (frontend) — por defecto http://localhost:3001</td></tr>
                  <tr><td className="px-4 py-2 font-mono text-xs">VITE_API_KEY</td><td className="px-4 py-2 text-xs text-muted-foreground">Debe coincidir con API_KEY del backend</td></tr>
                </tbody>
              </table>
            </div>
          </section>

        </div>
      </div>
    </>
  )
}
